/**
 * Stamp the library's auto-apply watermarks onto a document the first time it
 * is opened (VE-24693, review round 2).
 *
 * "The first time" means the file has NEVER BEEN SAVED, and the signal for that
 * already exists: `openDocument` reads `lastSavedAt` from the `createdDateTime`
 * of the newest `document-redaction-data` asset, for the editor's "Last saved"
 * indicator, and null means nobody has saved this document. Nothing new is
 * plumbed to decide WHETHER to stamp — the artwork fetch below is about what to
 * stamp, not when. (One asset is deliberately not counted: the stub the
 * upload path writes for lexicons picked in the upload dialog carries a
 * `writtenBy` marker and leaves `lastSavedAt` null — see VE-24948 review B4,
 * where counting it silently stopped this saga stamping anything on any
 * document uploaded with a lexicon.)
 *
 * A document saved even once is never stamped again, whatever the library says
 * — its own watermarks win from then on. That includes a document saved with no
 * redactions at all: it still has an asset, so it counts as saved, and it does.
 * The operator opened it and saved it; re-stamping it later would be the app
 * overruling that.
 *
 * WHAT IT STAMPS IS NOT AN UNSAVED CHANGE. The mark is dispatched with
 * `{ auto: true }`, which tells the reducer to add it without dirtying the
 * session. Nobody asked for it, so nobody should have to answer for it: before
 * this, opening a file and closing it again — touching nothing — produced
 * "This document has unsaved changes. Leave anyways?", an edit the app had
 * made on the reviewer's behalf handed back to them as work they were about to
 * discard. It also meant autosave wrote a redaction asset, minutes later, for a
 * document nobody had touched.
 *
 * That the mark can therefore be lost is the point, not a gap: the whole
 * decision to stamp is re-made from `lastSavedAt` on the next open, so a
 * document closed without saving is simply stamped again. And "lost" overstates
 * it even within the session — the mark draws, it undoes, and both export and a
 * manual save persist it, because those write the session snapshot whatever its
 * status. The only thing given up is autosave, which is the one writer here
 * that acts on nobody's instruction.
 *
 * It waits on TWO things, for the same underlying reason — this saga writes
 * into shared mutable state it does not own, so it re-validates rather than
 * assuming. The library may still be in flight (below). And the watermark panel
 * may be open: since VE-24693's preview rework, `ADD_WATERMARK` is routed into
 * `watermarkPreview` while one exists, so dispatching then would put this mark
 * somewhere Cancel discards silently — the document left unstamped while the
 * audit event claimed otherwise. Both waits are bounded, and everything is
 * re-checked afterwards: which document, and whether it has acquired marks.
 *
 * It also RESOLVES THE ARTWORK before stamping, rather than reading whatever
 * raster the definition happens to carry inline. A library image no longer
 * lives in the SDO — it is an asset on its own TDO (`imageTdoId`), and a
 * definition that keeps it there has no `imageBaseDataUrl` to read. Stamping
 * such a definition produces an image mark with nothing to draw: the overlay
 * renders nothing, the upload saga skips it, and the document is saved with a
 * watermark that cannot be reconstituted — which is what the operator meets on
 * the next open as "N watermark images could not be restored". A definition
 * whose artwork will not resolve is SKIPPED and audited, which is what the
 * operator-driven path (`applyLibraryWatermark`) does when it refuses.
 *
 * IT ALSO DEFERS TO THE SET (VE-30104). A member of a merged document draws
 * the set's watermarks, so a library entry the set already carries is skipped
 * rather than stamped: the release stays marked, once, by the set.
 *
 * This is a saga of its own rather than a step inside `openDocument` because
 * the two things it needs arrive independently. The watermark library is
 * fetched fire-and-forget by the route saga, deliberately, so that it cannot
 * delay OPEN_DOCUMENT — which means it may still be in flight when the document
 * opens. Waiting for it here costs the operator nothing; waiting for it there
 * would cost every operator a slower open for a feature most libraries will not
 * even use.
 */
import {
  call,
  delay,
  put,
  race,
  select,
  take,
  takeLatest,
} from 'typed-redux-saga/macro';
import { isNil } from 'lodash';
import { v4 as uuid } from 'uuid';

import { sagaIntl } from '@i18n';
import { enqueueSnackbar } from '@common-modules/snackbar';

import {
  ADD_WATERMARK,
  CLEAR_WATERMARK_PREVIEW,
  COMMIT_WATERMARK_PREVIEW,
  LOG_AUDIT_EVENT,
  OPEN_DOCUMENT_SUCCESS,
} from '../actions';
import {
  selectCommittedWatermarks,
  selectSession,
  selectWatermarkPreview,
} from '../selectors';
import {
  selectFeatureFlags,
  selectRolePermissions,
} from '@common/user-permissions';
import { selectGraphQLConfig, selectToken } from './auth';
import { deriveWatermarkImage } from '../watermarkImage';
import { selectWatermarkGovernanceFor } from '@redact-modules/projects/renderedWatermarks';
import { appliedFromDefinition } from '@redact-modules/watermarks/models';
import { hydrateDefinitionArtwork } from '@redact-modules/watermarks/artwork';
import { WatermarkAssetConfig } from '@redact-modules/watermarks/services/imageAssets';
import {
  FETCH_WATERMARKS_FAILURE,
  FETCH_WATERMARKS_SUCCESS,
} from '@redact-modules/watermarks/actions';
import {
  selectIsWatermarkFetchInProgress,
  selectPickableWatermarks,
} from '@redact-modules/watermarks/selectors';

/** How long to wait for the library before giving up and stamping nothing. */
const LIBRARY_WAIT_MS = 10_000;

/**
 * How long to wait for an open watermark panel to finish before giving up.
 *
 * Shorter than the library wait on purpose: that one waits on a request, this
 * one waits on a person. A panel still open after this is a session someone
 * walked away from, and a mark that lands minutes later — on top of whatever
 * they did next — is worse than one that never arrived.
 */
const PREVIEW_WAIT_MS = 30_000;

/**
 * Credentials the artwork fetch needs. A three-line copy of the watermarks
 * saga's helper rather than an import, because that module imports this one's
 * siblings and reaching across for a selector tuple would buy a cycle.
 */
function* assetConfig(): Generator<unknown, WatermarkAssetConfig, unknown> {
  const token = yield* select(selectToken);
  const { apiRoot, graphQLEndpoint, veritoneAppId } =
    yield* select(selectGraphQLConfig);
  return { token, apiRoot, graphQLEndpoint, veritoneAppId };
}

export function* autoApplyOnOpen(
  action: ReturnType<typeof OPEN_DOCUMENT_SUCCESS>
): Generator<unknown, void, unknown> {
  const { tdoId } = action.payload;

  // Saved before — the document's own watermarks are the answer.
  if (!isNil(action.payload.lastSavedAt)) {
    return;
  }

  // The feature's kill switch has to reach the one path that acts without
  // being asked. `exportPush` gates the OUTPUT on this flag, which stops a
  // disabled org's exports changing — but it does not stop this saga putting
  // marks into the session, and they outlive the flag: the operator's first
  // real edit and save persists whatever is sitting in `session.watermarks`
  // alongside it. An org that turned watermarks off would still accumulate them
  // on its documents, invisibly, until someone turned the flag back on.
  //
  // These are the RAW flags, which is only safe because of where this runs.
  // `enableDocumentRedaction` arrives with the org application config after
  // boot, so on a reload it reads false for every org until that lands — and
  // this saga hangs off OPEN_DOCUMENT_SUCCESS, which `initDocumentEditor` now
  // cannot reach until the config has loaded (VE-31015). Before that gate, a
  // document opened by reload or deep link could skip its org's watermark
  // silently, with nothing to retry it. Anything that adds a second path to
  // OPEN_DOCUMENT, or moves this off OPEN_DOCUMENT_SUCCESS, reopens that.
  const flags = yield* select(selectFeatureFlags);
  if (!flags.enableWatermarks || !flags.enableDocumentRedaction) {
    return;
  }

  // The same permission the editor gates its own controls on.
  //
  // `autoSave.ts` states the precondition it relies upon: "A view-only user
  // can't dirty the session or trigger a manual save, so no explicit gate is
  // needed here." This saga is a store-level watcher — it never sees `canEdit`,
  // which is derived in a component — so without this check that sentence
  // became false the moment this file existed. A reviewer opening a locked
  // document read-only would have had their browser write a redaction asset for
  // it five minutes later, over the top of whoever holds the lock. SEC-R5 is
  // explicit that this must not rest on hidden UI.
  const permissions = yield* select(selectRolePermissions);
  if (!permissions.saveEditFiles) {
    return;
  }
  // Defensive, and cheap: a never-saved document should carry no marks, but
  // stamping on top of marks that are somehow already there would be the one
  // outcome nobody could explain.
  const existing = yield* select(selectCommittedWatermarks);
  if (existing.length > 0) {
    return;
  }

  // The library is fetched fire-and-forget; if it is still in flight, wait for
  // it. Bounded, because a hung request must not leave this saga holding a
  // watcher slot for the life of the session.
  if (yield* select(selectIsWatermarkFetchInProgress)) {
    const settled = yield* race({
      ok: take(FETCH_WATERMARKS_SUCCESS),
      failed: take(FETCH_WATERMARKS_FAILURE),
      timeout: delay(LIBRARY_WAIT_MS),
    });
    if (!settled.ok) {
      return;
    }
    // Re-check what we are looking at, not just what we were told.
    //
    // The wait above can span a document change: `takeLatest` cancels a run
    // that is still parked, but a run that has already resumed is beyond its
    // reach, and `ADD_WATERMARK` writes into whichever session is current — its
    // reducer guards that one EXISTS, not which one it is. Without this, a
    // never-saved document A parking on the library and an operator moving to a
    // saved document B meant B got stamped, marked dirty, and autosaved. Both
    // guards above ran against A.
    const session = yield* select(selectSession);
    if (!session || session.tdoId !== tdoId) {
      return;
    }
    // Stale for the same reason, so re-read it rather than trusting the value
    // from before the wait.
    const nowApplied = yield* select(selectCommittedWatermarks);
    if (nowApplied.length > 0) {
      return;
    }
  }

  // The watermark panel previews into `watermarkPreview`, and while one exists
  // the reducer routes ADD_WATERMARK there — so dispatching now would put this
  // mark somewhere Cancel silently discards, leaving the document unstamped
  // while the event below claimed otherwise. Wait for the panel to finish
  // instead: the operator is in the middle of choosing marks, and this one is
  // supposed to be underneath whatever they choose.
  if (yield* select(selectWatermarkPreview)) {
    const closed = yield* race({
      applied: take(COMMIT_WATERMARK_PREVIEW),
      cancelled: take(CLEAR_WATERMARK_PREVIEW),
      timeout: delay(PREVIEW_WAIT_MS),
    });
    if (closed.timeout) {
      // Never write blind. A panel open this long is a session left sitting,
      // and an auto-apply that lands minutes later — over whatever the operator
      // has since done — is worse than one that did not happen.
      return;
    }
    // Everything re-checked after a wait, for the reasons above: the document
    // may have changed under us, and the visit we waited for may have applied
    // marks of its own.
    const after = yield* select(selectSession);
    if (!after || after.tdoId !== tdoId) {
      return;
    }
    if ((yield* select(selectCommittedWatermarks)).length > 0) {
      return;
    }
    // The reviewer switched watermarks off in the panel we waited on: that is
    // their answer for this document, and stamping marks onto it would override
    // it.
    if (after.watermarksOff) {
      return;
    }
  }

  // `selectPickableWatermarks` excludes deleted, disabled, and image
  // definitions with no artwork ANYWHERE — the same set a picker may offer.
  // Auto-apply must not be able to stamp something the operator could not have
  // chosen.
  //
  // "Anywhere" is doing real work in that sentence and this saga used to miss
  // it. The selector admits a definition whose raster lives in `imageTdoId` or
  // `previewDataUrl`, not only one carrying `imageBaseDataUrl` inline — so
  // passing the filter does NOT mean the row has artwork in hand. It has to be
  // fetched, which is what `hydrateDefinitionArtwork` below does.
  const definitions = yield* select(selectPickableWatermarks);
  const auto = definitions.filter((d) => d.data.autoApply === true);
  if (auto.length === 0) {
    return;
  }

  const cfg = yield* assetConfig();

  /**
   * WHAT THE SET ALREADY MARKS THIS FILE WITH (VE-30104).
   *
   * A member of a merged document draws the set's watermarks, so auto-applying
   * a library entry the set already carries produces a mark that is suppressed
   * the moment it lands: it never draws, never burns, and the panel hides the
   * row outright. That is a correct outcome for a duplicate the operator
   * made — their copy comes back if the set's goes — and for one nobody asked
   * for it is a mark that exists only to be invisible.
   *
   * So the set's entries are skipped here instead. The release is still
   * marked — by the set, once, rather than by every member separately — and
   * nothing is lost: if the set's copy is later removed, the next open of an
   * unsaved document auto-applies as it always would.
   */
  const governedBySet = yield* select(selectWatermarkGovernanceFor(tdoId));
  const setLibraryIds = new Set(
    (governedBySet.status === 'governed' ? governedBySet.marks : [])
      .map((w) => w.watermarkId)
      .filter((id): id is string => !!id)
  );

  for (const def of auto) {
    if (setLibraryIds.has(def.id)) {
      continue;
    }
    // Fetch the raster before building the mark, exactly as
    // `applyLibraryWatermark` does for the path the operator drives.
    //
    // This is what was missing. The library's artwork moved out of the SDO to
    // its own TDO, and the two other apply paths were taught to resolve it
    // while this one kept reading `data.imageBaseDataUrl` directly. For a
    // definition stored the new way that field is empty, so this produced an
    // image mark with no raster: the upload saga skipped it (`!added
    // .imageBaseDataUrl` — its comment calls that "a caller bug", and this was
    // the caller), `watermarksForStorage` had no asset id to point at and no
    // base to keep, and the body was written carrying a mark with neither.
    //
    // Nothing said so. The overlay draws nothing for a rasterless image mark,
    // the panel falls back to the library's preview thumbnail so the row looks
    // healthy, and the only report is a snackbar on the NEXT open saying some
    // number of images "could not be restored". By then the document had been
    // exported without its marking.
    let data = def.data;
    try {
      data = yield* call(hydrateDefinitionArtwork, def.data, cfg);
    } catch (err) {
      console.warn('Auto-applied watermark artwork could not be loaded', err);
    }
    if (data.type === 'image' && !data.imageBaseDataUrl) {
      // Skipped, not stamped empty. Applying it anyway is the behaviour this
      // change exists to remove — a mark that cannot be drawn cannot be burned
      // in, and a redaction product must not report a marking it did not make.
      console.warn(
        'Auto-apply skipped a watermark whose artwork could not be loaded',
        def.id
      );
      yield* put(
        LOG_AUDIT_EVENT({
          action: `Auto-apply skipped watermark ${JSON.stringify({
            definitionId: def.id,
            reason: 'artwork unavailable',
          })}`,
        })
      );
      // AND said on screen, which the audit log does not do. Nobody CHOSE this
      // mark, but somebody is sitting in front of the document and is about to
      // export it — and unlike the already-stamped case there is no row in the
      // watermark panel to warn them with, because the mark was never added.
      // The audit log answers "what happened to this document?" later; this
      // answers "should I export this now?", and only one of those two
      // questions is being asked at this moment.
      //
      // The operator-driven path raises the same sentence when it refuses
      // (`applyLibraryWatermark`), so the two paths fail identically from where
      // the operator sits.
      yield* put(
        enqueueSnackbar({
          message: sagaIntl().formatMessage({
            id: 'watermarkArtworkUnavailable',
            defaultMessage:
              'This watermark\u2019s image could not be loaded, so it was not applied.',
          }),
          variant: 'error',
        })
      );
      continue;
    }
    const applied = appliedFromDefinition(uuid(), def.id, data);
    if (applied.type === 'image' && applied.imageBaseDataUrl) {
      // Derived rather than left as the colour base, for the same reason
      // `openDocument` derives on restore: a definition whose default is black
      // & white would otherwise be stamped in colour, and the operator would
      // see the setting they chose ignored on the one path they did not drive.
      try {
        applied.imageDataUrl = yield* call(
          deriveWatermarkImage,
          applied.imageBaseDataUrl,
          { grayscale: applied.grayscale, color: applied.color }
        );
      } catch (err) {
        // Best-effort, matching the restore path: the mark keeps the colour
        // base rather than being dropped.
        console.warn('Auto-applied watermark image could not be derived', err);
      }
    }
    // The ordinary action, so this lands in the undo stack — an operator who
    // does not want the mark should be able to undo it the way they would undo
    // adding it themselves.
    //
    // `auto` is what stops it dirtying the document (see the reducer). Nobody
    // asked for this mark, so nobody should be asked to answer for it on the
    // way out: the reducer's comment has the full reasoning, and the header
    // above has what it costs.
    yield* put(ADD_WATERMARK(applied, { auto: true }));
    // Verify it landed on the DOCUMENT before claiming so. The guards above
    // make a preview redirect unreachable here, but this event is the only
    // record that a mark arrived with no operator action, and an audit trail
    // that can be wrong in the "we marked it" direction is worse than none.
    const committed = yield* select(selectCommittedWatermarks);
    if (!committed.some((w) => w.instanceId === applied.instanceId)) {
      continue;
    }
    // Audited, because this is the only path in the feature that changes a
    // document with no operator action, and the definition that caused it is
    // writable through the public API by any org member. Without a record, a
    // mark that arrives this way is indistinguishable from one the operator
    // applied, and the only attribution available — the definition's
    // `createdBy` — is un-corroborated self-report, which is worth capturing
    // precisely because it is a claim rather than a fact.
    yield* put(
      LOG_AUDIT_EVENT({
        action: `Auto-applied watermark ${JSON.stringify({
          watermarkId: def.id,
          name: def.data.watermarkName,
          claimedCreatedBy: def.data.createdBy ?? null,
        })}`,
      })
    );
  }
}

export default function* watermarkAutoApplySaga() {
  // `takeLatest`, like every other OPEN_DOCUMENT_SUCCESS handler in this module
  // (`runPii`, `pdfDimsPrepass`, `pdfPrepass`). This one was the outlier, and
  // being the outlier cost two defects: opening a document twice while the
  // library was in flight stamped it twice, because both runs cleared the
  // "already has marks" guard before either of them waited.
  yield* takeLatest(OPEN_DOCUMENT_SUCCESS, autoApplyOnOpen);
}
